import { Button, Form, FormField, Header, Input, SpaceBetween, FileUpload } from "@cloudscape-design/components";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Asset } from "../../types";

export interface AssetFormProps {
    asset?: Asset;
    onSubmit: (data: FormData | {
        asset: Asset,
        fileContent?: string,
        contentType?: string
    }) => void;
    type?: 'add' | 'edit';
    isSubmitting: boolean;
    setIsSubmitting: (isSubmitting: boolean) => void;
}

export default function AssetForm({ asset, onSubmit, type = 'add', isSubmitting, setIsSubmitting }: AssetFormProps) {
    const navigate = useNavigate();
    const [path, setPath] = useState(asset?.path || '');
    const [note, setNote] = useState(asset?.note || '');
    const [file, setFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState('');


    const handleSubmit = useCallback(async (e: any) => {
        e.preventDefault();
        if (type === 'add' && !file) {
            setFileError('Please select a file to upload');
            return;
        }

        setIsSubmitting(true);
        try {
            if (type === 'add') {
                // For new assets
                const formData = new FormData();
                formData.append('asset', JSON.stringify({
                    path,
                    note,
                    fileType: file?.type || '',
                    uploadedAt: new Date().toISOString(),
                    lastModified: new Date().toISOString(),
                }));
                if (file) {
                    formData.append('file', file);
                }
    
                onSubmit(formData);
            } else {
                // For editing
                const assetData = {
                    path,
                    note,
                    fileType: file?.type || asset?.fileType || '',
                    uploadedAt: asset?.uploadedAt || new Date().toISOString(),
                    lastModified: new Date().toISOString(),
                };
    
                const formData = new FormData();
                formData.append('asset', JSON.stringify(assetData));
                if (file) {
                    formData.append('file', file);
                }
    
                onSubmit(formData);
            }
        } finally {
            setIsSubmitting(false);
        }
    }, [path, note, file, type, onSubmit, asset?.fileType, asset?.uploadedAt, setIsSubmitting]);

    return (
        <Form
            actions={
                <SpaceBetween direction="horizontal" size="xs">
                    <Button formAction="none" variant="link" onClick={() => navigate('/admin')}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Uploading...' : type === 'add' ? 'Add asset' : 'Save changes'}
                    </Button>
                </SpaceBetween>
            }
            header={
                <Header variant="h1">
                    {type === 'add' ? 'Add new asset collection' : 'Edit asset collection'}
                </Header>
            }
        >
            <SpaceBetween direction="vertical" size="l">
                {type === 'add' && (
                    <FormField
                        label="File"
                        errorText={fileError}
                        description="Select a file to upload"
                    >
                        <FileUpload
                            onChange={({ detail }) => {
                                const uploadedFile = detail.value[0];
                                setFile(uploadedFile);
                                if (uploadedFile) {
                                    // Set default path based on filename
                                    const safeName = uploadedFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
                                    // Ensure path starts with assets/ (no leading slash)
                                    setPath(`assets/${safeName}`);
                                    setFileError('');
                                }
                            }}
                            value={file ? [file] : []}
                            i18nStrings={{
                                uploadButtonText: _e => "Choose file",
                                removeFileAriaLabel: e => `Remove ${e}`,
                                limitShowFewer: "Show fewer files",
                                limitShowMore: "Show more files",
                                errorIconAriaLabel: "Error"
                            }}
                            constraintText="File will be uploaded to S3"
                            multiple={false}
                        />
                    </FormField>
                )}

                <FormField label="Path" description="Asset path in S3 (automatically populated from filename)">
                    <Input
                        value={path}
                        onChange={({ detail }) => {
                            let newPath = detail.value;
                            // Remove any leading slashes and ensure starts with assets/
                            newPath = newPath.replace(/^\/+/, '').replace(/^assets\//, '');
                            newPath = `assets/${newPath}`;
                            setPath(newPath);
                        }}
                    />
                </FormField>

                <FormField label="Note" description="Add a description for this asset">
                    <Input
                        value={note}
                        onChange={({ detail }) => setNote(detail.value)}
                    />
                </FormField>
            </SpaceBetween>
        </Form>
    );
}
